import type { RootState } from '@/store';
import { SelectedLocationStateSelector } from '@/store/selectors';
import {
  clearSelectedLocation,
  setAppointmentsData,
  setAppointmentsError,
  setAppointmentsLoading,
  setSelectedLocation,
  setUserLocationsData,
  setUserLocationsError,
  setUserLocationsLoading,
  type UserLocation,
} from '@/store/slices/dataSlice';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

/**
 * Custom hook for managing data operations
 * Currently handles appointments and user locations data
 */
export const useData = () => {
  const dispatch = useDispatch();
  const appointments = useSelector(
    (state: RootState) => state.data.appointments
  );
  const userLocations = useSelector(
    (state: RootState) => state.data.userLocations
  );
  const selectedLocationId = useSelector(
    SelectedLocationStateSelector.locationId
  );
  const selectedLocationName = useSelector(
    SelectedLocationStateSelector.locationName
  );

  // Appointments methods
  const setAppointments = useCallback(
    (data: any) => {
      dispatch(setAppointmentsData(data));
    },
    [dispatch]
  );

  const setAppointmentsLoadingState = useCallback(
    (loading: boolean) => {
      dispatch(setAppointmentsLoading(loading));
    },
    [dispatch]
  );

  const setAppointmentsErrorState = useCallback(
    (error: string) => {
      dispatch(setAppointmentsError(error));
    },
    [dispatch]
  );

  // User Locations methods
  const setUserLocations = useCallback(
    (data: UserLocation[]) => {
      dispatch(setUserLocationsData(data));
    },
    [dispatch]
  );

  const setUserLocationsLoadingState = useCallback(
    (loading: boolean) => {
      dispatch(setUserLocationsLoading(loading));
    },
    [dispatch]
  );

  const setUserLocationsErrorState = useCallback(
    (error: string) => {
      dispatch(setUserLocationsError(error));
    },
    [dispatch]
  );

  // Selected Location methods
  const setSelectedLocationState = useCallback(
    (locationId: string, locationName: string) => {
      dispatch(setSelectedLocation({ locationId, locationName }));
    },
    [dispatch]
  );

  const clearSelectedLocationState = useCallback(() => {
    dispatch(clearSelectedLocation());
  }, [dispatch]);

  return {
    // Appointments
    appointments: appointments.data,
    appointmentsLoading: appointments.loading,
    appointmentsError: appointments.error,
    setAppointments,
    setAppointmentsLoading: setAppointmentsLoadingState,
    setAppointmentsError: setAppointmentsErrorState,

    // User Locations
    userLocations: userLocations.data,
    userLocationsLoading: userLocations.loading,
    userLocationsError: userLocations.error,
    userLocationsFetched: userLocations.fetched,
    setUserLocations,
    setUserLocationsLoading: setUserLocationsLoadingState,
    setUserLocationsError: setUserLocationsErrorState,

    // Selected Location
    selectedLocation: userLocations.data
      ? userLocations.data.find((loc) => loc.locationId === selectedLocationId)
      : null,
    selectedLocationId,
    selectedLocationName,
    setSelectedLocation: setSelectedLocationState,
    clearSelectedLocation: clearSelectedLocationState,
  };
};
